import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TICKET_STATUS, TICKET_CATEGORIES, TICKET_PRIORITY } from "@/lib/tickets";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Ticket as TicketIcon, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";

export function PortalTickets() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    const { data } = await api.get("/tickets");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Meine Tickets</h1>
          <p className="text-sm text-slate-500 mt-1">Anliegen und Schäden melden</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="portal-new-ticket">
              <Plus size={16} /> Neues Ticket
            </Button>
          </DialogTrigger>
          <NewTicketDialog onSaved={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {items.length === 0 && (
          <div className="p-10 text-center">
            <TicketIcon size={36} className="mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500">Sie haben noch keine Tickets eingereicht.</p>
          </div>
        )}
        {items.map((t) => {
          const s = TICKET_STATUS[t.status];
          const p = TICKET_PRIORITY[t.priority];
          return (
            <button key={t.id} onClick={() => setActive(t)}
              className="w-full text-left px-5 py-4 border-b border-slate-100 hover:bg-slate-50 flex items-start justify-between gap-3"
              data-testid={`portal-ticket-${t.id}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-slate-500">{t.ticket_number}</span>
                  <span className="text-xs text-slate-500">·</span>
                  <span className="text-xs text-slate-600">{TICKET_CATEGORIES[t.category]}</span>
                </div>
                <div className="text-sm font-medium text-slate-900">{t.title}</div>
                {t.description && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{t.description}</div>}
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
                  <span>Eingereicht {formatDate(t.created_at)}</span>
                  {t.comments?.length > 0 && (
                    <span className="flex items-center gap-0.5"><ChatCircleDots size={11} />{t.comments.length}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1 items-end">
                <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${s.className}`}>{s.label}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.className}`}>{p.label}</span>
              </div>
            </button>
          );
        })}
      </div>

      {active && <TicketDialog ticket={active} onClose={() => setActive(null)} onChanged={() => { load(); setActive(null); }} />}
    </div>
  );
}

function NewTicketDialog({ onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", category: "other", priority: "medium" });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", form);
      toast.success("Ticket eingereicht");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>Neues Ticket einreichen</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <F label="Titel"><Input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="portal-ticket-title" /></F>
        <F label="Beschreibung"><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Bitte beschreiben Sie das Anliegen so genau wie möglich..." data-testid="portal-ticket-desc" /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Kategorie">
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="portal-ticket-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TICKET_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Priorität">
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TICKET_PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
        </div>
        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="portal-ticket-save">Einreichen</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function TicketDialog({ ticket, onClose, onChanged }) {
  const [data, setData] = useState(ticket);
  const [comment, setComment] = useState("");
  useEffect(() => { setData(ticket); }, [ticket]);
  const send = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tickets/${data.id}/comments`, { text: comment, is_internal: false });
      const r = await api.get(`/tickets/${data.id}`);
      setData(r.data);
      setComment("");
      toast.success("Kommentar gesendet");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  const s = TICKET_STATUS[data.status];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-slate-500">{data.ticket_number}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.className}`}>{s.label}</span>
          </div>
          <DialogTitle>{data.title}</DialogTitle>
        </DialogHeader>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm text-slate-700 whitespace-pre-wrap">
          {data.description}
        </div>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {(data.comments || []).map((c) => (
            <div key={c.id} className="p-2.5 rounded-sm border border-slate-200 bg-white text-sm">
              <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                <span>{c.author_name || "Verwaltung"}</span>
                <span>{formatDate(c.created_at)}</span>
              </div>
              <div className="text-slate-800 whitespace-pre-wrap">{c.text}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 items-end">
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Antwort..." />
          <Button onClick={send} className="bg-slate-900 hover:bg-slate-800">Senden</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div><Label className="text-xs text-slate-700">{label}</Label><div className="mt-1">{children}</div></div>;
}
